import type { ReactNode } from "react";
import type { DealListItemDto, DealPipelineStage, DealSubState } from "@/types/deals";
import { STAGE_LABEL } from "@/types/deals";
import { cn } from "@/design-system/lib/utils";
import { STAGE_ACCENT } from "./dealFormat";
import { SubStateSection } from "./SubStateSection";
import type { DealCardAction } from "./DealCard";

export interface DroppedDealPayload {
  id: string;
  fromStage: DealPipelineStage;
  fromSubState: DealSubState;
  toStage: DealPipelineStage;
  toSubState: DealSubState;
}

export interface KanbanLookups {
  brokerName?: (deal: DealListItemDto) => string | null | undefined;
  assigneeName?: (deal: DealListItemDto) => string | null | undefined;
  hasOverdueDocs?: (deal: DealListItemDto) => boolean;
  hasMissingFields?: (deal: DealListItemDto) => boolean;
}

export interface SubStateBucket {
  /** When null, the section renders without a sub-state header — used
   *  for stages that don't define sub-states (Underwriting / Closing /
   *  Closed) so any cards in those stages still render. */
  subState: DealSubState | null;
  items: DealListItemDto[];
}

export interface KanbanColumnProps {
  stage: DealPipelineStage;
  totalCount: number;
  sections: SubStateBucket[];
  /** When true, sections in this column accept drops. */
  acceptsDrops?: boolean;
  onDrop?: (payload: DroppedDealPayload) => void;
  cardActions?: DealCardAction[];
  lookups?: KanbanLookups;
  /** When the entire stage is empty, this replaces the section list. */
  emptyState?: ReactNode;
  /** Hide sub-state sections that have zero items. Default: false. */
  hideEmptySubstates?: boolean;
  /** Grow to share the available width equally instead of a fixed
   *  320px column. Used by the unified pipeline board so the stages
   *  fill the viewport; still scrolls horizontally below the min width. */
  fill?: boolean;
}

/** A single stage column. Sub-states stack vertically as section headers
 *  inside the column — replaces the legacy parallel swim-lane layout that
 *  collapsed labels at typical viewport widths. */
export function KanbanColumn({
  stage,
  totalCount,
  sections,
  acceptsDrops = false,
  onDrop,
  cardActions,
  lookups,
  emptyState,
  hideEmptySubstates = false,
  fill = false,
}: KanbanColumnProps) {
  const accent = STAGE_ACCENT[stage];
  const visibleSections = hideEmptySubstates
    ? sections.filter((s) => s.items.length > 0)
    : sections;

  return (
    <div
      className={cn(
        "flex flex-col gap-space-3",
        fill ? "flex-1 min-w-[280px]" : "flex-shrink-0 min-w-[300px] w-[320px]",
        "bg-white-warm border border-border-light rounded-md",
      )}
      data-stage={stage}
      role="group"
      aria-label={`${STAGE_LABEL[stage]} column`}
    >
      <div
        className={cn(
          "sticky top-0 z-10 px-space-3 py-space-2 rounded-t-md",
          "bg-white-pure border-b border-border-light",
          "flex items-center justify-between",
        )}
      >
        <div className="flex items-center gap-space-2">
          <span className={cn("inline-block h-3 w-3 rounded-full", accent.dot)} />
          <h3
            className={cn(
              "font-display font-bold text-label uppercase tracking-label m-0",
              accent.text,
            )}
          >
            {STAGE_LABEL[stage]}
          </h3>
        </div>
        <span
          className="font-mono text-caption text-gray-mid tabular-nums"
          aria-label={`${totalCount} deals total`}
        >
          · {totalCount}
        </span>
      </div>

      <div className="flex flex-col gap-space-3 px-space-2 pb-space-3">
        {totalCount === 0 ? (
          (emptyState ?? (
            <div className="text-caption italic text-gray-mid px-space-2 py-space-4 text-center">
              No {STAGE_LABEL[stage]} deals yet
            </div>
          ))
        ) : visibleSections.length === 0 ? (
          <div className="text-caption italic text-gray-mid px-space-2 py-space-3 text-center">
            All sub-states empty — toggle off "Hide empty" to view.
          </div>
        ) : (
          visibleSections.map((section, idx) => (
            <SubStateSection
              key={section.subState ?? `__default__${idx}`}
              stage={stage}
              subState={section.subState}
              items={section.items}
              acceptsDrops={acceptsDrops}
              onDrop={onDrop}
              cardActions={cardActions}
              lookups={lookups}
            />
          ))
        )}
      </div>
    </div>
  );
}
